import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';

import { DATE_FILTERS } from '@shared/contants/variables';
import { OtaPerformanceService } from '@services/ota-performance.service';
import { IOtaPerformanceResponse } from '@shared/interfaces/ota-performance.interface';
import { PageHeaderComponent } from '@shared/components/page-header/page-header.component';

import { Chart } from 'chart.js';
import { ChartModule } from 'primeng/chart';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import ChartDataLabels from 'chartjs-plugin-datalabels';

@Component({
    selector: 'app-ota-report',
    standalone: true,
    imports: [CommonModule, ChartModule, TableModule, CardModule, PageHeaderComponent],
    templateUrl: './ota-report.component.html',
    styleUrl: './ota-report.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OtaReportComponent implements OnInit {
    otaPerformanceService = inject(OtaPerformanceService);
    filterDateSearch = signal<string>('');
    beginDate = signal<string>('');
    endDate = signal<string>('');

    otaPerformanceData = signal<IOtaPerformanceResponse | null>(null);

    priceRequestChartData = signal<any>({
        labels: [],
        datasets: [
            {
                label: 'Price Request',
                data: [],
                backgroundColor: '#B3CFE5',
                borderColor: '#90CAF9',
                borderWidth: 1,
                borderRadius: 8,
                barPercentage: 0.4
            }
        ]
    });

    bookRequestChartData = signal<any>({
        labels: [],
        datasets: [
            {
                label: 'Book Request',
                data: [],
                backgroundColor: '#015CA6',
                borderColor: '#1976D2',
                borderWidth: 1,
                borderRadius: 8,
                barPercentage: 0.4
            }
        ]
    });

    lowFareRequestChartData = signal<any>({
        labels: [],
        datasets: [
            {
                label: 'Low Fare Request',
                data: [],
                backgroundColor: '#4CAF50',
                borderColor: '#388E3C',
                borderWidth: 1,
                borderRadius: 8,
                barPercentage: 0.4
            }
        ]
    });

    lowFareV2RequestChartData = signal<any>({
        labels: [],
        datasets: [
            {
                label: 'Low Fare V2 Request',
                data: [],
                backgroundColor: '#FF9800',
                borderColor: '#F57C00',
                borderWidth: 1,
                borderRadius: 8,
                barPercentage: 0.4
            }
        ]
    });

    priceRequestChartOptions = {
        responsive: true,
        plugins: {
            legend: { display: false },
            datalabels: {
                anchor: 'end',
                align: 'top',
                color: '#000',
                font: { weight: 'bold', size: 12 }
            } as any
        },
        scales: {
            x: {
                title: { display: true, text: 'Agent Name', font: { size: 14, weight: 'bold' } },
                grid: { display: false }
            },
            y: {
                title: { display: true, text: 'Price Request Count', font: { size: 14, weight: 'bold' } },
                beginAtZero: true
            }
        }
    };

    bookRequestChartOptions = {
        responsive: true,
        plugins: {
            legend: { display: false },
            datalabels: {
                anchor: 'end',
                align: 'top',
                color: '#000',
                font: { weight: 'bold', size: 12 }
            } as any
        },
        scales: {
            x: {
                title: { display: true, text: 'Agent Name', font: { size: 14, weight: 'bold' } },
                grid: { display: false }
            },
            y: {
                title: { display: true, text: 'Book Request Count', font: { size: 14, weight: 'bold' } },
                beginAtZero: true
            }
        }
    };

    lowFareRequestChartOptions = {
        responsive: true,
        plugins: {
            legend: { display: false },
            datalabels: {
                anchor: 'end',
                align: 'top',
                color: '#000',
                font: { weight: 'bold', size: 12 }
            } as any
        },
        scales: {
            x: {
                title: { display: true, text: 'Agent Name', font: { size: 14, weight: 'bold' } },
                grid: { display: false }
            },
            y: {
                title: { display: true, text: 'Low Fare Request Count', font: { size: 14, weight: 'bold' } },
                beginAtZero: true
            }
        }
    };

    lowFareV2RequestChartOptions = {
        responsive: true,
        plugins: {
            legend: { display: false },
            datalabels: {
                anchor: 'end',
                align: 'top',
                color: '#000',
                font: { weight: 'bold', size: 12 }
            } as any
        },
        scales: {
            x: {
                title: { display: true, text: 'Agent Name', font: { size: 14, weight: 'bold' } },
                grid: { display: false }
            },
            y: {
                title: { display: true, text: 'Low Fare V2 Request Count', font: { size: 14, weight: 'bold' } },
                beginAtZero: true
            }
        }
    };

    constructor() {
        Chart.register(ChartDataLabels);
    }

    ngOnInit() {
        this.filterDateSearch.set(sessionStorage.getItem(DATE_FILTERS.FLIGHT_DATE_SEARCH) || '');
        this.beginDate.set(sessionStorage.getItem(DATE_FILTERS.BEGIN_DATE) || '');
        this.endDate.set(sessionStorage.getItem(DATE_FILTERS.END_DATE) || '');

        this.getOtaPerformance();
    }

    getOtaPerformance() {
        this.otaPerformanceService.getOtaPerformance(this.beginDate(), this.endDate()).subscribe({
            next: (response: any) => {
                this.otaPerformanceData.set(response);
                this.setChartData(response);
            },
            error: (error) => {
                console.log(error);
            }
        });
    }

    setChartData(response: any) {
        const priceRequestsLabels = Object.keys(response.priceRequests || {});
        const bookRequestsLabels = Object.keys(response.bookRequests || {});
        const lowFareRequestsLabels = Object.keys(response.lowFareRequests || {});
        const lowFareV2RequestsLabels = Object.keys(response.lowFareV2Requests || {});

        const priceValues = Object.values(response.priceRequests || {});
        const bookValues = Object.values(response.bookRequests || {});
        const lowFareValues = Object.values(response.lowFareRequests || {});
        const lowFareV2Values = Object.values(response.lowFareV2Requests || {});

        this.priceRequestChartData.set({
            ...this.priceRequestChartData(),
            labels: priceRequestsLabels,
            datasets: this.priceRequestChartData().datasets.map((dataset: any) => ({
                ...dataset,
                data: dataset.label === 'Price Request' ? priceValues : []
            }))
        });

        this.bookRequestChartData.set({
            ...this.bookRequestChartData(),
            labels: bookRequestsLabels,
            datasets: this.bookRequestChartData().datasets.map((dataset: any) => ({
                ...dataset,
                data: dataset.label === 'Book Request' ? bookValues : []
            }))
        });

        this.lowFareRequestChartData.set({
            ...this.lowFareRequestChartData(),
            labels: lowFareRequestsLabels,
            datasets: this.lowFareRequestChartData().datasets.map((dataset: any) => ({
                ...dataset,
                data: dataset.label === 'Low Fare Request' ? lowFareValues : []
            }))
        });

        this.lowFareV2RequestChartData.set({
            ...this.lowFareV2RequestChartData(),
            labels: lowFareV2RequestsLabels,
            datasets: this.lowFareV2RequestChartData().datasets.map((dataset: any) => ({
                ...dataset,
                data: dataset.label === 'Low Fare V2 Request' ? lowFareV2Values : []
            }))
        });
    }
}
